import React, { useEffect, useMemo, useState } from 'react';
import {
  buildRuntimeFields,
  cleanupHiddenValues,
  computeVisibilityMap,
  normalizeCountryOptions,
} from './engine.js';

function KycFormRenderer({
  fields,
  countries = [],
  containerClassName = 'border rounded p-3 bg-light',
  emptyText = 'No fields configured.',
  showHeader = true,
  headerText = 'Form Preview',
}) {
  const runtimeFields = useMemo(() => buildRuntimeFields(fields), [fields]);
  const [values, setValues] = useState({});
  const clientLocale = typeof navigator !== 'undefined' && navigator.language
    ? navigator.language
    : 'en-US';

  useEffect(() => {
    const validKeys = new Set(runtimeFields.map((field) => field.key));
    setValues((previous) => {
      const next = { ...previous };
      let changed = false;

      Object.keys(next).forEach((key) => {
        if (!validKeys.has(key)) {
          delete next[key];
          changed = true;
        }
      });

      return changed ? next : previous;
    });
  }, [runtimeFields]);

  const visibilityMap = useMemo(
    () => computeVisibilityMap(runtimeFields, values),
    [runtimeFields, values],
  );

  useEffect(() => {
    setValues((previous) => cleanupHiddenValues(previous, visibilityMap));
  }, [visibilityMap]);

  const countryOptions = useMemo(() => normalizeCountryOptions(countries), [countries]);

  const updateValue = (key, value) => {
    setValues((previous) => ({ ...previous, [key]: value }));
  };

  return (
    <div className={containerClassName}>
      {showHeader && <div className="mb-3 small text-muted">{headerText}</div>}
      {runtimeFields.length === 0 && <div className="text-muted">{emptyText}</div>}

      {runtimeFields.map((field, fieldIndex) => {
        if (!visibilityMap[field.key]) {
          return null;
        }

        const required = field.required && visibilityMap[field.key];
        const baseLabel = (
          <>
            {field.name}
            {required ? ' *' : ''}
          </>
        );

        if (field.type === 'select' || field.type === 'country') {
          const options = field.type === 'country' ? countryOptions : field.options;

          return (
            <div className="mb-3" key={`${field.key}-${fieldIndex}`}>
              <label className="form-label">{baseLabel}</label>
              <select
                className="form-select"
                value={String(values[field.key] ?? '')}
                onChange={(event) => updateValue(field.key, event.target.value)}
                required={required}
              >
                <option value="">Please select</option>
                {options.map((option) => (
                  <option key={`${field.key}-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          );
        }

        if (field.type === 'radio') {
          return (
            <div className="mb-3" key={`${field.key}-${fieldIndex}`}>
              <label className="form-label d-block">{baseLabel}</label>
              {field.options.map((option) => (
                <div className="form-check" key={`${field.key}-${option.value}`}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name={field.key}
                    value={option.value}
                    checked={String(values[field.key] ?? '') === option.value}
                    onChange={(event) => updateValue(field.key, event.target.value)}
                    required={required}
                  />
                  <label className="form-check-label">{option.label}</label>
                </div>
              ))}
            </div>
          );
        }

        if (field.type === 'checkbox') {
          return (
            <div className="mb-3 form-check" key={`${field.key}-${fieldIndex}`}>
              <input
                className="form-check-input"
                type="checkbox"
                checked={Boolean(values[field.key])}
                onChange={(event) => updateValue(field.key, event.target.checked)}
                required={required}
              />
              <label className="form-check-label">{baseLabel}</label>
            </div>
          );
        }

        if (field.type === 'date') {
          return (
            <div className="mb-3" key={`${field.key}-${fieldIndex}`}>
              <label className="form-label">{baseLabel}</label>
              <input
                type="date"
                className="form-control"
                lang={clientLocale}
                value={String(values[field.key] ?? '')}
                onChange={(event) => updateValue(field.key, event.target.value)}
                required={required}
              />
            </div>
          );
        }

        return (
          <div className="mb-3" key={`${field.key}-${fieldIndex}`}>
            <label className="form-label">{baseLabel}</label>
            <input
              className="form-control"
              placeholder={field.placeholder}
              value={String(values[field.key] ?? '')}
              onChange={(event) => updateValue(field.key, event.target.value)}
              required={required}
            />
          </div>
        );
      })}
    </div>
  );
}

export default KycFormRenderer;
